import { DatePipe, NgClass, NgStyle, SlicePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbDatepickerModule, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { PostTitlePipe, getDocumentTitle } from '@zero/postbox/ui-pipes';
import { expandTdDivsAnimation, fadeInAnimation, onlyEnter } from '@zero/shared/ui-animations';
import { LoadingIndicatorDirective } from '@zero/shared/ui-directives';
import { SortHelper, UserMessagesComponent, UserNotesComponent } from '@zero/shared/ui-web-components';
import { isEqual } from '@zero/shared/util-helper';
import { Post } from '@zero/shared/util-models';
import { DataService, UserMessagesHolderService, WindowService } from '@zero/shared/util-web-app-services';
import { environment } from 'environments/environment';
import { assign, differenceBy, intersectionBy, isEqualWith, pullAllBy, remove } from 'lodash-es';
import { Subscription, firstValueFrom, of } from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';
import { PdfUriPipe } from '../../pipes/post-uri/pdf-uri.pipe';

@Component({
  templateUrl: './post-box-page.component.html',
  animations: [fadeInAnimation, expandTdDivsAnimation, onlyEnter],
  imports: [
    UserNotesComponent,
    LoadingIndicatorDirective,
    FormsModule,
    NgbDatepickerModule,
    NgClass,
    NgStyle,
    UserMessagesComponent,
    SlicePipe,
    DatePipe,
    PostTitlePipe,
    PdfUriPipe,
    TranslocoPipe,
  ],
})
export class PostBoxPageComponent implements OnInit, OnDestroy {
  public showPost: boolean;

  public category: string;

  public sorter: SortHelper;
  public filteredDocuments: Post[] = new Array<Post>();
  readonly #INITIALDOCNUMBER = 10;
  public max = this.#INITIALDOCNUMBER;
  public searchTerm = '';
  public vonDatum: Date;
  public bisDatum: Date;

  // subscribed events
  public updating = false;

  public vonDatumDatepickerClosed = true;
  public vonDatumDatepickerClosed$ = new Subscription();
  public bisDatumDatepickerClosed = true;
  public bisDatumDatepickerClosed$ = new Subscription();

  public readonly currentYear = new Date().getFullYear();

  public readonly dataService = inject(DataService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #httpClient = inject(HttpClient);
  readonly #userMessagesHolderService = inject(UserMessagesHolderService);
  readonly #windowService = inject(WindowService);

  constructor() {
    this.sorter = new SortHelper('date', false, 'inbox');
  }

  ngOnInit(): void {
    this.showPost = true;
    this.category = 'P';
    this.dataService.selectedCustomerChanged
      .pipe(
        filter((newCust) => !!newCust),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.#loadDocs(true);
      });

    this.dataService.unreadDocumentsCountChanged$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.#loadDocs(true);
    });
  }

  ngOnDestroy(): void {
    if (this.vonDatumDatepickerClosed$ && !this.vonDatumDatepickerClosed$.closed) {
      this.vonDatumDatepickerClosed$.unsubscribe();
    }
    if (this.bisDatumDatepickerClosed$ && !this.bisDatumDatepickerClosed$.closed) {
      this.bisDatumDatepickerClosed$.unsubscribe();
    }
  }

  public sortBy(sortAttr: string, resort?: boolean): void {
    this.filter();

    if (resort === true) {
      this.sorter.sortValues(this.filteredDocuments);
    } else {
      this.sorter.sortBy(this.filteredDocuments, sortAttr);
    }
  }

  public archive(p: Post): void {
    if (p.archived) {
      return;
    }
    p.archived = true;
    if (!p.read) {
      p.readAt = new Date();
      p.read = true;
    }

    // remove from unarchived, add to archived, keep order by id
    const sourceCategory = p.mifid ? 'M' : 'P';
    const targetCategory = 'A';
    const docInRoot = this.dataService.documents.get(sourceCategory).find((doc) => doc.id === p.id);
    Object.assign(docInRoot, p);
    this.updating = true;
    this.updatePost(p)
      .then((data: Post) => {
        p.archived = data.archived;
        p.read = data.read;
        p.readAt = data.readAt;
        const removed = remove(this.dataService.documents.get(sourceCategory), (post) => post.id === p.id);

        if (this.dataService.documents.get(targetCategory)) {
          this.dataService.documents.get(targetCategory).push(removed[0]);
          this.dataService.documents.get(targetCategory).sort((a, b) => Number(a.id) - Number(b.id));
        }
        remove(this.filteredDocuments, (post) => post.id === p.id);
        this.dataService.triggerReloadUnreadDocuments();
        this.updating = false;
      })
      .catch((err) => {
        this.#userMessagesHolderService.addErrorMessage(err);
        p.archived = false;
        this.updating = false;
      });
  }

  public updatePost(p: Post): Promise<any> {
    return firstValueFrom(this.#httpClient.post(environment.apihost + environment.posteingangServiceUrl, p));
  }

  public toggleGelesen(p: Post): void {
    if (!p.read) {
      p.readAt = new Date();
      p.read = true;
    } else {
      p.readAt = null;
      p.read = false;
    }
    this.updating = true;
    this.updatePost(p)
      .then((data: Post) => {
        p.read = data.read;
        p.readAt = data.readAt;
        this.dataService.triggerReloadUnreadDocuments();
        this.updating = false;
      })
      .catch((err) => {
        this.#userMessagesHolderService.addErrorMessage(err);
        p.read = !p.read;
        if (!p.read) {
          p.readAt = null;
        }
        this.updating = false;
      });
  }

  public setGelesen(p: Post): void {
    if (!p.read) {
      p.readAt = new Date();
      p.read = true;
      this.updating = true;
      this.updatePost(p)
        .then((data: Post) => {
          p.read = data.read;
          p.readAt = data.readAt;
          this.dataService.triggerReloadUnreadDocuments();
          this.updating = false;
        })
        .catch((err) => {
          this.#userMessagesHolderService.addErrorMessage(err);
          p.read = !p.read;
          if (!p.read) {
            p.readAt = null;
          }
          this.updating = false;
        });
    }
  }

  public mehr(): void {
    this.max = this.max + this.#INITIALDOCNUMBER;
  }

  public searchChanged(): void {
    this.filter();
  }

  public filter(): void {
    let filteredDocs = this.dataService.documents.get(this.category);
    if (this.vonDatum) {
      const vonMillis = this.vonDatum.getTime() + this.vonDatum.getTimezoneOffset() * 60 * 1000;
      filteredDocs = filteredDocs.filter((p1) => p1.date && Date.parse(p1.date) >= vonMillis);
    }
    if (this.bisDatum) {
      const bisMillis = this.bisDatum.getTime() + this.bisDatum.getTimezoneOffset() * 60 * 1000;
      filteredDocs = filteredDocs.filter((p1) => p1.date && Date.parse(p1.date) <= bisMillis);
    }
    if (this.searchTerm) {
      const terms: string[] = this.searchTerm.toLowerCase().split(' ');
      terms.forEach((term) => (filteredDocs = filteredDocs.filter((p1) => getDocumentTitle(p1).toLowerCase().includes(term))));
    }

    this.mergeDocuments(filteredDocs);
    this.sorter.sortValues(this.filteredDocuments);
  }

  public changeCategory(category: string): void {
    this.category = category;
    this.#loadDocs();
    this.max = this.#INITIALDOCNUMBER;
  }

  #loadDocs(forceReload?: boolean): void {
    this.updating = true;
    this.dataService
      .loadDocuments(this.category, forceReload)
      .then(() => {
        this.sortBy('date', true);
        this.updating = false;
      })
      .catch(() => {
        this.updating = false;
      });
  }

  public setAllRead(category: string): void {
    this.#setAllArchivedOrRead(category, false);
  }

  public setAllArchived(category: string): void {
    this.#setAllArchivedOrRead(category, true);
    this.#windowService.getWindow().scrollTo(0, 0);
  }

  #setAllArchivedOrRead(category: string, archived: boolean): void {
    this.updating = true;
    const urlPart = archived === true ? '/setarchived?t=' : '/setread?t=';
    this.#httpClient
      .post(
        `${environment.apihost + environment.posteingangServiceUrl}/${this.dataService.getSelectedCustomer()}${urlPart}${category}`,
        null,
      )
      .pipe(
        catchError(() => {
          this.updating = false;
          return of(undefined);
        }),
      )
      .subscribe(() => {
        if (archived === true) {
          // delete archived ones as well so they have to be reloaded
          this.dataService.documents.delete('A');
        }
        // force reload of current category
        this.dataService.documents.delete(category);
        this.changeCategory(category); // this also reloads the documents

        this.dataService.triggerReloadUnreadDocuments();
        this.updating = false;
      });
  }

  #comparatorForMerge = (o1: Post, o2: Post) =>
    isEqual(o1.read, o2.read) && isEqual(o1.archived, o2.archived) && isEqual(o1.readAt, o2.readAt);

  public mergeDocuments(docsToMerge: Post[]): void {
    const mergePropertyName = 'id';
    const toRemove = differenceBy(this.filteredDocuments, docsToMerge, mergePropertyName);
    const toAdd = differenceBy(docsToMerge, this.filteredDocuments, mergePropertyName);
    pullAllBy(this.filteredDocuments, toRemove, mergePropertyName);
    this.filteredDocuments.push(...toAdd);
    const docIdsToUpdate = intersectionBy(this.filteredDocuments, docsToMerge, mergePropertyName).map((doc) => doc[mergePropertyName]);
    this.filteredDocuments.forEach((currDoc) => {
      if (docIdsToUpdate.indexOf(currDoc[mergePropertyName]) > -1) {
        const docToMerge = docsToMerge.find((o) => o[mergePropertyName] === currDoc[mergePropertyName]);
        if (!isEqualWith(currDoc, docToMerge, this.#comparatorForMerge)) {
          assign(currDoc, docToMerge);
        }
      }
    });
  }

  public openDatenpicker(ev: FocusEvent, d: NgbInputDatepicker, dateInput: HTMLInputElement, dpName: string): void {
    if (d && !d.isOpen() && this[`${dpName}DatepickerClosed`] === true) {
      this[`${dpName}DatepickerClosed`] = false;
      d.open();
      if (this[`${dpName}DatepickerClosed$`] && !this[`${dpName}DatepickerClosed$`].closed) {
        this[`${dpName}DatepickerClosed$`].unsubscribe();
      }
      d.closed.pipe(take(1)).subscribe(() => {
        Promise.resolve().then(() => {
          this[`${dpName}DatepickerClosed`] = true;
          if (dateInput && typeof dateInput.blur === 'function') {
            dateInput.blur();
          }
        });
      });
    }
  }
}
